import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  Cluster,
  EmptyState,
  Field,
  GlobeIcon,
  Input,
  Page,
  QueryState,
  Select,
  ShieldIcon,
  Stack,
  StatStrip,
  TextArea,
  TranslateIcon,
} from '@integra/ui';
import {
  useAssets,
  useAssetTranslations,
  useRemoveTranslation,
  useSetTranslation,
} from '../api/hooks.js';

/**
 * Localization & translation (§20.5, Activate). One hero asset ships in the right
 * language on each market's channel: pick an asset, add a per-locale translation
 * of its content fields. Every change is audited (the record of what wording goes
 * out where).
 */

const LOCALES: { code: string; name: string }[] = [
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'pt-BR', name: 'Portuguese (Brazil)' },
  { code: 'it', name: 'Italian' },
  { code: 'nl', name: 'Dutch' },
  { code: 'ja', name: 'Japanese' },
  { code: 'zh', name: 'Chinese' },
  { code: 'ar', name: 'Arabic' },
];

function localeName(code: string): string {
  return LOCALES.find((l) => l.code === code)?.name ?? code;
}

function readString(data: Record<string, unknown>, key: string): string {
  const v = data[key];
  return typeof v === 'string' ? v : '';
}

export function LocalizationPage() {
  const { workspaceId = '' } = useParams<{ workspaceId: string }>();
  const assets = useAssets(workspaceId);
  const [assetId, setAssetId] = useState('');
  const translations = useAssetTranslations(workspaceId, assetId);
  const setTranslation = useSetTranslation(workspaceId, assetId);
  const removeTranslation = useRemoveTranslation(workspaceId, assetId);

  const [locale, setLocale] = useState('es');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');

  const assetList = assets.data?.data ?? [];
  const selected = assetList.find((a) => a.id === assetId);
  const sourceTitle = selected ? readString(selected.data, 'title') : '';
  const sourceBody = selected
    ? readString(selected.data, 'body') ||
      readString(selected.data, 'caption') ||
      readString(selected.data, 'description')
    : '';

  // Seed the editor from an existing translation when the locale changes.
  const existing = (translations.data?.data ?? []).find((t) => t.locale === locale);
  const existingTitle = existing?.fields['title'] ?? '';
  const existingBody = existing?.fields['body'] ?? '';
  useEffect(() => {
    setTitle(existingTitle);
    setBody(existingBody);
  }, [locale, assetId, existingTitle, existingBody]);

  function save(): void {
    if (assetId === '') return;
    const fields: Record<string, string> = {};
    if (title.trim() !== '') fields['title'] = title.trim();
    if (body.trim() !== '') fields['body'] = body.trim();
    setTranslation.mutate({ locale, fields });
  }

  const list = translations.data?.data ?? [];

  return (
    <Page
      title="Localization"
      icon={TranslateIcon}
      description="Ship one asset in every market's language. Add a per-locale translation of an asset's content — audited, and ready to publish to the right channel."
      stats={
        list.length > 0 ? (
          <StatStrip
            stats={[
              { icon: TranslateIcon, label: 'Translations', value: list.length, tone: 'accent' },
              { icon: GlobeIcon, label: 'Locales available', value: LOCALES.length },
            ]}
          />
        ) : undefined
      }
    >
      <Stack gap="lg">
        <Card title="Choose an asset" subtitle="Translations attach to a source asset's content.">
          <Field label="Asset" htmlFor="loc-asset">
            <Select id="loc-asset" value={assetId} onChange={(e) => setAssetId(e.target.value)}>
              <option value="">Choose an asset…</option>
              {assetList.map((a) => (
                <option key={a.id} value={a.id}>
                  {readString(a.data, 'title') || a.id} · {a.type}
                </option>
              ))}
            </Select>
          </Field>
        </Card>

        {assetId !== '' && (
          <>
            <Card
              title="Add or edit a translation"
              subtitle="Leave a field blank to fall back to the source wording."
            >
              <Stack gap="md">
                <div className="rounded-md border border-edge bg-surface-sunken px-4 py-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
                    Source (original)
                  </p>
                  <p className="mt-1 text-sm font-semibold text-navy-700">
                    {sourceTitle || '(no title)'}
                  </p>
                  {sourceBody !== '' && (
                    <p className="mt-0.5 text-xs text-ink-muted">{sourceBody}</p>
                  )}
                </div>

                <Field label="Locale" htmlFor="loc-locale">
                  <Select
                    id="loc-locale"
                    value={locale}
                    onChange={(e) => setLocale(e.target.value)}
                  >
                    {LOCALES.map((l) => (
                      <option key={l.code} value={l.code}>
                        {l.name} ({l.code})
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Title" htmlFor="loc-title">
                  <Input id="loc-title" value={title} onChange={(e) => setTitle(e.target.value)} />
                </Field>
                <Field label="Body" htmlFor="loc-body">
                  <TextArea
                    id="loc-body"
                    rows={4}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                  />
                </Field>
                <Cluster justify="end">
                  <Button loading={setTranslation.isPending} onClick={save}>
                    Save {localeName(locale)} translation
                  </Button>
                </Cluster>
                {setTranslation.error instanceof Error && (
                  <Alert tone="error" title="Could not save the translation">
                    {setTranslation.error.message}
                  </Alert>
                )}
              </Stack>
            </Card>

            <Card title="Translations">
              <QueryState
                loading={translations.isLoading}
                error={translations.error}
                what="translations"
              >
                {list.length === 0 ? (
                  <EmptyState
                    icon={TranslateIcon}
                    title="No translations yet"
                    description="Add one above to ship this asset in another language."
                    features={[
                      { icon: GlobeIcon, label: 'One asset, every market’s language' },
                      { icon: TranslateIcon, label: 'Per-locale title & body overrides' },
                      { icon: ShieldIcon, label: 'Every wording change is audited' },
                    ]}
                  />
                ) : (
                  <Stack gap="sm">
                    {list.map((t) => (
                      <div
                        key={t.id}
                        className="flex items-start gap-3 rounded-md border border-edge bg-surface px-3 py-2.5"
                      >
                        <Badge tone="info">{localeName(t.locale)}</Badge>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-navy-700">
                            {t.fields['title'] ?? '(title unchanged)'}
                          </p>
                          {t.fields['body'] !== undefined && (
                            <p className="truncate text-xs text-ink-muted">{t.fields['body']}</p>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          loading={
                            removeTranslation.isPending && removeTranslation.variables === t.locale
                          }
                          onClick={() => removeTranslation.mutate(t.locale)}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </Stack>
                )}
              </QueryState>
            </Card>
          </>
        )}
      </Stack>
    </Page>
  );
}
